import Link from "next/link";

/**
 * Perché fidarsi, sotto il catalogo.
 *
 * Sta al posto delle testimonianze, e la ragione non è solo che inventarle è
 * vietato: **non funzionano**. Chi compra pezzi su specifica per la propria
 * produzione non si convince con «Marco da Bergamo, ottimo servizio», si
 * convince sapendo chi risponde se il lotto arriva sbagliato. Le testimonianze
 * anonime le legge come rumore, e su un sito nuovo le legge come sospetto.
 *
 * Ogni riga qui sotto è una cosa che il sistema **fa davvero**, con il posto in
 * cui è scritta accanto. Non sono promesse di marketing: sono garanzie che
 * altrove nel codice sono trigger, viste e vincoli. Se una smettesse di essere
 * vera, va tolta da qui prima che dal database.
 */

const PUNTI: { titolo: string; testo: string; dove?: { href: string; testo: string } }[] = [
  {
    titolo: "Compri da noi, non da uno sconosciuto",
    testo:
      "Non ti mettiamo in contatto con un produttore lasciandoti sbrigare il resto: " +
      "il contratto è con noi, la fattura è nostra, e se qualcosa va storto c'è una " +
      "controparte sola a cui rivolgersi.",
    dove: { href: "/come-funziona", testo: "Come funziona" },
  },
  {
    titolo: "Non ordiniamo a chi non è assicurato",
    testo:
      "Nessuna commessa parte verso un produttore senza una polizza di responsabilità " +
      "civile prodotti verificata e in corso di validità. Il controllo non è una prassi " +
      "interna: è il database che rifiuta l'ordine.",
  },
  {
    titolo: "Il prezzo che vedi è quello che paghi",
    testo:
      "Il prezzo si calcola in un punto solo del sistema, e da lì passano la scheda, il " +
      "carrello e la fattura. Non esistono maggiorazioni che compaiono al passo dopo.",
    dove: { href: "/condizioni-di-vendita", testo: "Condizioni di vendita" },
  },
  {
    titolo: "Le valutazioni vengono solo da chi ha comprato",
    testo:
      "Non c'è un modulo aperto a chiunque: si valuta una riga d'ordine consegnata, una " +
      "volta sola. Le recensioni che oggi trovi sui prodotti sono nostre e dimostrative, " +
      "e lo dicono su ogni scheda finché non ci saranno quelle vere.",
  },
  {
    titolo: "Il tuo disegno resta tuo",
    testo:
      "Quello che carichi serve a quotare e a far produrre quella commessa. Va al " +
      "produttore solo se l'ordine parte, non finisce in nessuna vetrina, e le bozze " +
      "abbandonate si cancellano dopo novanta giorni con i loro allegati.",
    dove: { href: "/informativa-privacy", testo: "Informativa privacy" },
  },
  {
    titolo: "Il sito non ti segue",
    testo:
      "Nessuno strumento di statistica, nessun profilo, nessuna risorsa caricata da " +
      "terzi finché non sei tu a chiederlo. È per questo che non trovi un banner: non " +
      "c'è niente da accettare.",
  },
];

export function Fiducia({ className = "" }: { className?: string }) {
  return (
    <section className={className} aria-labelledby="fiducia">
      <h2 id="fiducia" className="px-1 text-sm font-medium">
        Perché comprare da noi
      </h2>
      <p className="mt-1 px-1 text-sm text-muted">
        Siamo nuovi e non abbiamo ancora clienti da citare. Al posto delle frasi di
        qualcuno, le cose che il sistema fa e che puoi verificare.
      </p>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {PUNTI.map((p) => (
          <li
            key={p.titolo}
            className="rounded-xl border border-line bg-surface p-4"
          >
            <h3 className="text-sm font-semibold">{p.titolo}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted">{p.testo}</p>
            {p.dove && (
              <Link
                href={p.dove.href}
                className="mt-2 inline-block text-sm text-accent underline-offset-4 hover:underline"
              >
                {p.dove.testo}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
